import { Plus, TriangleAlert } from 'lucide-react';
import React, { useState } from 'react';
// Importer les types API BlockerRead, BlockerCreate, BlockerUpdate
import { BlockerRead, BlockerCreate, BlockerUpdate } from '../../types';
import BlockerCard from './BlockerCard';
import BlockerEditModal from './BlockerEditModal';
 // Ajouter TriangleAlert
// Importer BlockerFormData du hook
import { BlockerFormData } from '../../hooks/useCadrageBlockers';
import { useTranslation } from 'react-i18next';

// Mettre à jour les props pour utiliser les types API
interface BlockerManagerProps {
  cadrageId: string;
  blockers: BlockerRead[]; // Utiliser BlockerRead
  isLoading: boolean;
  error: string | null;
  addBlocker: (formData: BlockerCreate) => Promise<BlockerRead | null>; // Utiliser BlockerCreate/Read
  updateBlocker: (blockerId: string, formData: BlockerUpdate) => Promise<BlockerRead | null>; // Utiliser BlockerUpdate/Read
  deleteBlocker: (blockerId: string) => Promise<void>;
  isReadOnly?: boolean; // Mode lecture seule
}

const BlockerManager: React.FC<BlockerManagerProps> = ({
  cadrageId, // Utiliser cadrageId
  blockers,
  isLoading,
  error,
  addBlocker,
  updateBlocker,
  deleteBlocker,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  // Supprimer l'état local des blockers
  // const [blockers, setBlockers] = useState<Blocker[]>([]);
  // Supprimer l'état non utilisé
  // const [newBlockerDescription, setNewBlockerDescription] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [blockerToEdit, setBlockerToEdit] = useState<BlockerRead | null>(null); // Utiliser BlockerRead
  const [isAdding, setIsAdding] = useState(false);
  // const [submissionError, setSubmissionError] = useState<string | null>(null); // Temporairement commenté car non utilisé

  // Ouvre la modale pour l'ajout
  const handleOpenAddModal = () => {
    setBlockerToEdit(null); // Assure qu'aucun blocker n'est en édition
    setIsAdding(true); // Indique qu'on est en mode ajout
    setIsEditModalOpen(true);
  };

  // Gère la suppression d'un verrou en appelant la prop
  const handleDeleteBlocker = async (blockerId: string) => {
      // La confirmation peut être ajoutée ici ou dans BlockerCard
      if (!window.confirm(t('blocker.deleteConfirm'))) return;
      // setSubmissionError(null); // Clear previous errors
      try {
          await deleteBlocker(blockerId);
          // La liste est rafraîchie par le hook parent
          // Fermer la modale si le verrou supprimé était en cours d'édition
          if (blockerToEdit?.id === blockerId) {
              setIsEditModalOpen(false);
              setBlockerToEdit(null);
          }
      } catch (err) {
          console.error(t('blocker.errors.deleteError'), err);
          // setSubmissionError(err instanceof Error ? err.message : "Erreur inconnue");
      }
  };

  // Ouvre la modale pour l'édition (attend BlockerRead)
  const handleEditBlocker = (blocker: BlockerRead) => {
    setBlockerToEdit(blocker);
    setIsAdding(false);
    setIsEditModalOpen(true);
  };

  // Gère la sauvegarde depuis la modale (ajout ou modification)
  // Gère la sauvegarde depuis la modale en appelant les props add/update
  // Gère la sauvegarde depuis la modale en appelant les props add/update
  // La modale passe BlockerFormData (ou un type similaire)
  const handleSaveChanges = async (dataFromModal: BlockerFormData) => { // Renommer et typer l'argument
      // setSubmissionError(null);

      // Préparer les données pour l'API (BlockerCreate ou BlockerUpdate)
      // On utilise directement dataFromModal car il correspond maintenant aux types API
      const payload = { ...dataFromModal };

      try {
          console.log("[BlockerManager] Données du formulaire reçues:", payload);
          if (isAdding) {
              // Mode Ajout - addBlocker attend BlockerCreate
              // Assurer que les champs optionnels sont bien null si vides
              const createPayload: BlockerCreate = {
                  description: payload.name, // Utiliser name comme description (c'est le champ principal)
                  criticite: payload.criticality === '' ? null : payload.criticality as 'faible' | 'moyenne' | 'haute' | null,
                  strategie_mitigation: payload.mitigation_strategy === '' ? null : payload.mitigation_strategy,
                  cadrage_id: cadrageId,
              };
              console.log("[BlockerManager] Payload CREATE:", createPayload);
              await addBlocker(createPayload);
          } else if (blockerToEdit) {
              // Mode Modification - updateBlocker attend BlockerUpdate
              const updatePayload: BlockerUpdate = {
                  description: payload.name, // Utiliser name comme description (c'est le champ principal)
                  criticite: payload.criticality === '' ? null : payload.criticality as 'faible' | 'moyenne' | 'haute' | null,
                  strategie_mitigation: payload.mitigation_strategy === '' ? null : payload.mitigation_strategy,
              };
              console.log("[BlockerManager] Payload UPDATE:", updatePayload);
              // S'assurer que les champs non modifiés ne sont pas envoyés si l'API attend une mise à jour partielle stricte
              // Pour l'instant, on envoie tout. L'API devrait ignorer les champs non modifiables si nécessaire.
              await updateBlocker(blockerToEdit.id, updatePayload);
          }
          setIsEditModalOpen(false);
          setBlockerToEdit(null);
          setIsAdding(false);
      } catch (err) {
          console.error(t('blocker.errors.saveError'), err);
          // setSubmissionError(err instanceof Error ? err.message : "Erreur inconnue");
          // Ne pas fermer la modale en cas d'erreur pour permettre la correction
      }
  };


  return (
    <div className="bg-card p-6 rounded-lg shadow-sm border border-border"> {/* Fond légèrement différent */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-card-foreground">
          {t('blocker.title')}
        </h3>
        {!isReadOnly && (
          <button
            onClick={handleOpenAddModal}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
            disabled={isLoading} // Désactiver si chargement global
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('blocker.addButton')}
          </button>
        )}
      </div>

      {/* Affichage en cartes */}
      <div className="mb-6">
        {/* Afficher l'erreur globale du hook */}
        {error && (
            <div className="mb-4 bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold mr-2"><TriangleAlert className="inline h-5 w-5 mr-1"/>{t('blocker.errors.loadError')}</strong>
                <span className="block sm:inline">{error}</span>
            </div>
        )}
        {/* Afficher l'état de chargement */}
        {isLoading && <p className="text-sm text-muted-foreground italic">{t('global.loading')}</p>}

        {/* Afficher la liste ou message si vide */}
        {!isLoading && blockers.length === 0 && !error && (
          <p className="text-sm text-muted-foreground italic">{t('blocker.empty')}</p>
        )}
        {!isLoading && blockers.length > 0 && (
          <div className="space-y-4">
            {blockers.map((blocker: BlockerRead) => ( // Typer blocker ici
              <BlockerCard
                key={blocker.id}
                blocker={blocker} // Passer BlockerRead à BlockerCard (adapter BlockerCard si besoin)
                onEdit={() => handleEditBlocker(blocker)} // Passer le blocker complet
                onDelete={() => handleDeleteBlocker(blocker.id)}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Le formulaire d'ajout direct est supprimé */}

      {/* Modale d'édition / ajout */}
      <BlockerEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setBlockerToEdit(null); // Assure la réinitialisation
          setIsAdding(false); // Réinitialise le mode
        }}
        blockerToEdit={blockerToEdit} // Passe BlockerRead | null
        onSave={handleSaveChanges} // Attend BlockerFormData
        isAdding={isAdding}
        // cadrageId={cadrageId} // Temporairement commenté car non utilisé
        // Retirer isLoading et error qui ne sont pas des props de BlockerEditModal
      />
    </div>
  );
};

export default BlockerManager;
